import { TutorialDefinition } from './tutorialDefinitions';

/**
 * Character progression tutorials - Phase 3 Implementation
 * Covers skill trees, talent points, and character advancement
 */

export const SKILL_TREE_TUTORIAL: TutorialDefinition = {
  key: 'skill-tree-basics',
  title: 'Skill Tree & Talent Points',
  description: 'Master character progression through the skill tree system',
  category: 'progression',
  priority: 'high',
  estimatedMinutes: 4,
  icon: '🌳',
  prerequisites: ['battle-basics'], // Should complete battle tutorial first
  steps: [
    {
      id: 'welcome',
      title: 'Welcome to the Skill Tree!',
      content: 'The skill tree is where you unlock powerful passive abilities and active skills to customize your character. Let\'s learn how to build your perfect hero!',
      position: 'center',
      type: 'info'
    },
    {
      id: 'talent-points',
      title: 'Talent Points',
      content: 'You earn 3 Talent Points every time you level up. These points are used to unlock skills in your skill tree. Your current talent points are shown at the top of the screen.',
      targetSelector: '.talent-points-display',
      position: 'bottom',
      type: 'info',
      highlightPadding: 12
    },
    {
      id: 'skill-nodes',
      title: 'Skill Nodes',
      content: 'Each circle on the tree is a skill node. Click on any skill to see its details, requirements, and effects. Skills are connected by lines showing their relationships.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'skill-types',
      title: 'Skill Types',
      content: 'There are different types of skills: 🛡️ Passive (always active stat bonuses), ⚔️ Active (combat abilities), 👑 Mastery (advanced passives), ✨ Signature (powerful unique abilities), and 🔧 Utility (helpful non-combat skills).',
      position: 'center',
      type: 'info'
    },
    {
      id: 'skill-requirements',
      title: 'Prerequisites & Requirements',
      content: 'Most skills require you to unlock other skills first (shown by connecting lines). Some also require a minimum character level. Green checkmarks mean requirements are met, red X\'s mean not yet.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'unlocking-skills',
      title: 'Unlocking Skills',
      content: 'To unlock a skill, click on it and press the "Unlock" button. This will spend 1 Talent Point. Passive skills activate immediately. Active skills must be equipped to use in battle.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'category-filters',
      title: 'Category Filters',
      content: 'Use the filter buttons at the top to show only certain types of skills. This helps you focus on the abilities you want to unlock without getting overwhelmed by choices.',
      targetSelector: '.skill-category-filters',
      position: 'bottom',
      type: 'info',
      highlightPadding: 8
    },
    {
      id: 'class-skills',
      title: 'Class-Specific Skills',
      content: 'Each character class (Fighter, Mage, Rogue) has unique skills. Fighters get combat skills, Mages get powerful spells, and Rogues get stealth and mobility abilities.',
      position: 'center',
      type: 'info'
    },
    {
      id: 'universal-skills',
      title: 'Universal Skills',
      content: 'Some skills are available to all classes. These are usually utility abilities like Meditation (MP regen), Perception (reveal enemies), or Endurance (HP boost).',
      position: 'center',
      type: 'info'
    },
    {
      id: 'planning-build',
      title: 'Planning Your Build',
      content: 'Think carefully before spending talent points! You can\'t refund them (yet). Focus on skills that complement your playstyle. Balanced builds are often stronger than specializing too early. Good luck, hero!',
      position: 'center',
      type: 'info'
    }
  ]
};

export const progressionTutorials = [SKILL_TREE_TUTORIAL];
